import React, { FunctionComponent, useState } from 'react'
import { Avatar, Button, Modal, Text } from '@afterpaytouch/core'
import { useAppDispatch, useAppRouter, useConsumerName, useConsumerSelector, authLogout, setAllowedQueryParam, useAllowedQuery } from '../../state'
import { Route, useRoutePageName } from '../../config/router'
import { useTranslation } from '../../utils/convergence'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { useCheckoutSizes } from '../../utils/responsive'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import styles from './style.module.css'

interface AccountModalProps {
  show: boolean
  onClose: () => void
}

export const AccountModal: FunctionComponent<AccountModalProps> = ({ show, onClose }) => {
  const { t } = useTranslation()
  const consumerName = useConsumerName()
  const { email } = useConsumerSelector()
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const { textSize, headingSize, smallTextSize } = useCheckoutSizes()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const pageName = useRoutePageName()
  const allowedQuery = useAllowedQuery()
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const onLogout = async (): Promise<void> => {
    setIsLoggingOut(true)
    logEvent(TrackingEvent.PRESSED_AUTO_REMEMBER_ME_ACCOUNT_MODAL_LOG_OUT_BUTTON, { pageName })
    const result = await dispatch(authLogout())
    if (authLogout.fulfilled.match(result)) {
      logEvent(TrackingEvent.REMEMBER_ME_REVOKED, { revokeReason: 'userRevoked', scope: 'this-device', pageName })
    }
    dispatch(setAllowedQueryParam({ ...allowedQuery, skipPrefill: true }))
    router.push(Route.ENTER_EMAIL)
  }

  if (!show) {
    return null
  }

  return (
    <Modal show={show} onClose={onClose} showCloseButton>
      <TrackOnMount eventName={TrackingEvent.VIEWED_AUTO_REMEMBER_ME_ACCOUNT_MODAL} eventProperties={{ pageName }} />
      <Modal.Content>
        <div className={styles.content} data-testid='auto-remember-me-account-modal'>
          <MaskedElement actionName='Auto Remember Me Account Modal'>
            <div className={styles.avatar}>
              <Avatar size='M' src='' alt={consumerName} name={consumerName} title={consumerName} />
            </div>
            <Text id='modal-label' size={headingSize} bold>
              {consumerName}
            </Text>
            <Text size={textSize}>{email}</Text>
          </MaskedElement>
          <Text size={smallTextSize}>{t('common:rememberMeAccountModalImplication')}</Text>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button kind='Primary' padding='Fluid' size='Large' onClick={onLogout} loading={isLoggingOut} testNameSpace='auto-remember-me-account-modal-logout'>
          {t('common:rememberMeAccountModalLogOut')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
